/// <reference types="vite/client" />
/**
 * Infrastructure HTTP Client
 * Production-ready Axios instance with interceptors, token injection, and error handling.
 */

import axios, { InternalAxiosRequestConfig } from 'axios';
import { errorBus } from '../../lib/errorBus';
import { clearAllLookupCaches } from '../../lib/lookupCacheService';
import { uiBus } from '../../lib/uiBus';
import { storageService } from '../storage/storageService';
import { telemetryService } from '../monitoring/telemetryService';
import { getActiveSocietyId } from '../../shared/utils/activeSociety';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipGlobalErrorHandler?: boolean;
    metadata?: {
      startTime?: number;
    };
  }
}

const SILENT_GLOBAL_ERROR_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/logout',
  '/auth/refresh',
  '/auth/otp',
  '/auth/forgot-password',
  '/auth/verify-reset-token',
  '/auth/reset-password',
  '/lookups',
  '/telemetry',
];

const BASE_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) || 'http://localhost:8080';

export const httpClient = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  timeout: 30_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return null;
  }
}

function isTokenExpiredOrInvalid(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  return payload.exp * 1000 < Date.now();
}

import { tokenManager } from '../security/tokenManager';

const isPublicRoute = (pathname: string): boolean => {
  return ['/login', '/signup', '/forgot-password', '/reset-password', '/p'].some(
    p => pathname === p || (p !== '/' && pathname.startsWith(p))
  ) || pathname === '/';
};

export function clearAuthStorage(): void {
  tokenManager.clearTokens();
  clearAllLookupCaches();
  if (typeof window !== 'undefined' && window.localStorage) {
    const keys = [
      'xservices_authenticated',
      'xservices_user_id',
      'xservices_user_uuid',
      'xservices_profile_id',
      'xservices_member_id',
      'xservices_member_type',
      'xservices_role',
      'xservices_permissions',
      'xservices_first_name',
      'xservices_last_name',
      'xservices_email',
      'xservices_email_verified',
      'xservices_society_id',
      'xservices_service_roles',
      'xservices_active_society_id',
      'xservices_token',
    ];
    keys.forEach((k) => window.localStorage.removeItem(k));
  }
}

function forceLogout(reason = 'Your session has expired. Please log in again.') {
  clearAuthStorage();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  if (!isPublicRoute(currentPath)) {
    errorBus.emit({ title: 'Session Expired', message: reason, code: 'ERR_401' });
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    }
  }
}

httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // 1. Client-Side Request Validation: Block calls with undefined/null/NaN path parameters
  if (config.url) {
    const invalidPathSegment = /\/(undefined|null|NaN)(\/|$|\?)/i;
    if (invalidPathSegment.test(config.url)) {
      return Promise.reject(
        new axios.Cancel(
          `[Client-Side Validation] Blocked invalid API call to "${config.url}": missing required path parameter.`
        )
      );
    }
  }

  uiBus.setLoading(true);
  config.metadata = { startTime: typeof performance !== 'undefined' ? performance.now() : Date.now() };

  // Volatile in-memory token fallback; primary session authenticated via HttpOnly cookie
  const token = tokenManager.getAccessToken();
  if (token) {
    if (isTokenExpiredOrInvalid(token)) {
      forceLogout();
      return Promise.reject(new axios.Cancel('Token expired — redirecting to login'));
    }
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Multi-society role scoping: inject active society header if selected
  const activeSocietyId = getActiveSocietyId();
  if (activeSocietyId && config.headers) {
    config.headers['X-Society-Id'] = String(activeSocietyId);
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => {
    uiBus.setLoading(false);
    const startTime = response.config?.metadata?.startTime;
    const durationMs = startTime ? (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime : 0;
    telemetryService.recordApiLatency({
      endpoint: response.config?.url || '',
      method: (response.config?.method || 'GET').toUpperCase(),
      durationMs,
      status: response.status,
      success: true,
    });
    return response;
  },
  async (error) => {
    uiBus.setLoading(false);

    // If request was canceled/aborted intentionally (TanStack Query, navigation, AbortSignal), ignore completely
    if (
      axios.isCancel(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError' ||
      error?.name === 'AbortError' ||
      error?.message === 'canceled'
    ) {
      return Promise.reject(error);
    }

    const startTime = error.config?.metadata?.startTime;
    const durationMs = startTime ? (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime : 0;
    const status = error.response?.status || 0;

    telemetryService.recordApiLatency({
      endpoint: error.config?.url || '',
      method: (error.config?.method || 'GET').toUpperCase(),
      durationMs,
      status,
      success: false,
      error: error.message,
    });

    if (!error.response) {
      telemetryService.recordNetworkFailure({
        url: error.config?.url || '',
        method: (error.config?.method || 'GET').toUpperCase(),
        errorType: error.code === 'ECONNABORTED' ? 'TIMEOUT' : 'OFFLINE',
        durationMs,
      });
    }

    const requestUrl: string = error.config?.url || '';

    const isSilentEndpoint = SILENT_GLOBAL_ERROR_ENDPOINTS.some((ep) => requestUrl.includes(ep));
    const skipGlobal = isSilentEndpoint || error.config?.skipGlobalErrorHandler === true;

    if (status === 401 && requestUrl.includes('/auth/refresh')) {
      clearAuthStorage();
    }

    if (!skipGlobal) {
      if (!error.response) {
        errorBus.emit({
          title: 'Connection Lost',
          message: 'Unable to connect to the server. Please check your internet connection or the server status.',
          code: 'NETWORK_ERR',
        });
      } else {
        const rawData = error.response.data;
        const status: number = error.response.status;

        let data = rawData;
        if (rawData instanceof Blob && rawData.type?.includes('application/json')) {
          try {
            const text = await rawData.text();
            data = JSON.parse(text);
          } catch {
            data = {};
          }
        }
        let message = data?.message || 'Wait! Something went wrong while processing your request.';

        if (status === 401) {
          clearAuthStorage();
          const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
          if (!isPublicRoute(currentPath)) {
            message = 'Your session has expired. Please log in again.';
            errorBus.emit({ title: 'Session Expired', message, code: 'ERR_401' });
            if (typeof window !== 'undefined') {
              setTimeout(() => {
                window.location.href = '/login';
              }, 1500);
            }
          }
          return Promise.reject(error);
        }

        if (status === 403) {
          errorBus.emit({
            title: 'Access Restricted',
            message: data?.message || 'You do not have permission to perform this action.',
            code: 'ERR_403',
          });
          return Promise.reject(error);
        }

        if (status === 404) message = 'The requested resource was not found.';
        if (status === 500) message = 'Our server encountered an internal error. Please try again later.';

        if (status === 400 && data?.details?.fieldErrors) {
          const fieldErrors: Record<string, string> = data.details.fieldErrors;
          const errLines = Object.entries(fieldErrors)
            .map(([field, msg]) => `• ${field}: ${msg}`)
            .join('\n');
          message = `${message}\n${errLines}`;
        }

        errorBus.emit({
          title: `Internal Error`,
          message: message,
          code: `ERR_${status}`,
        });
      }
    }

    return Promise.reject(error);
  }
);

export default httpClient;
